import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApiPathEnum,
  ChargesWithdrawalsRequestEnum,
} from '../constants/endpoints.enum';
import { ApiResponse } from '../interfaces/api-response.inteface';
import { ProfitPerPlayer } from '../interfaces/dashboard.interface';
import { DateRange, PaginationParams } from '../interfaces/filters.interface';
import {
  ChargesWithdrawalsRequestCountsData,
  ChargesWithdrawalsRequestFilters,
  ChargesWithdrawalsRequestItem,
  ChargesWithdrawalsRequestListData,
  DivideChargesWithdrawalsRequestPayload,
  RepeatedChargesWithdrawalsVoucherByDateRange,
  ReverseChargesWithdrawalsRequestPayload,
  SetChargesWithdrawalsRequestPayload,
  SetChargesWithdrawalsRequestResponse,
  SetChargesWithdrawalsRequestStatePayload,
} from '../interfaces/charges-withdrawals-request.interface';
import { UserCreatedRegisterResponse } from '../interfaces/agent-request.interface';

type MutationResponse = ApiResponse<null | undefined>;

@Injectable({
  providedIn: 'root',
})
export class ChargesWithdrawalsRequestService {
  private API_URL = `${environment.API_URL}${ApiPathEnum.ChargesWithdrawalsRequest}`;

  constructor(private http: HttpClient) {}

  getAllData(
    page: number,
    limit: number,
    filters: ChargesWithdrawalsRequestFilters = {}
  ): Observable<ApiResponse<ChargesWithdrawalsRequestListData>> {
    const params = this.buildFilterParams(filters)
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<ApiResponse<ChargesWithdrawalsRequestListData>>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.GetChargesWithdrawals}`,
      { params }
    );
  }

  getCounts(
    filters: ChargesWithdrawalsRequestFilters = {}
  ): Observable<ApiResponse<ChargesWithdrawalsRequestCountsData>> {
    return this.http.get<ApiResponse<ChargesWithdrawalsRequestCountsData>>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.GetChargesWithdrawalsCounts}`,
      { params: this.buildFilterParams(filters) }
    );
  }

  private buildFilterParams(filters: ChargesWithdrawalsRequestFilters): HttpParams {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((v) => { params = params.append(key, String(v)); });
      } else {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  getImgCw(cwId: number): Observable<ApiResponse<unknown>> {
    return this.http.get<ApiResponse<unknown>>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.GET_IMG_ENDPOINT}/${cwId}`
    );
  }

  getCw(cwId: number): Observable<ApiResponse<ChargesWithdrawalsRequestItem>> {
    return this.http.get<ApiResponse<ChargesWithdrawalsRequestItem>>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.GET_CW}/${cwId}`
    );
  }

  createRequest(formData: FormData): Observable<MutationResponse> {
    return this.http.post<MutationResponse>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.CreateRequest}`,
      formData
    );
  }

  setRequest(
    data: FormData | SetChargesWithdrawalsRequestPayload
  ): Observable<ApiResponse<SetChargesWithdrawalsRequestResponse>> {
    return this.http.patch<ApiResponse<SetChargesWithdrawalsRequestResponse>>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.SetRequest}`,
      data
    );
  }

  setState(
    cwId: number,
    newState: number,
    idusuarioSet: number,
    details?: string,
    transactionType?: number,
    allowRepeatedVoucher = false
  ): Observable<MutationResponse> {
    const body: SetChargesWithdrawalsRequestStatePayload = {
      id: cwId,
      estado: newState,
      id_usuario_set: idusuarioSet,
      detalle: details,
      tipo_transaccion: transactionType,
      allowRepeatedVoucher,
    };
    return this.http.patch<MutationResponse>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.SetState}`,
      body
    );
  }

  getPlayerTransactionsPerAgent(
    filters: PaginationParams &
      DateRange &
      ChargesWithdrawalsRequestFilters
  ): Observable<ApiResponse<{ data: ProfitPerPlayer[]; totalCount: number }>> {
    const endpoint = `${ChargesWithdrawalsRequestEnum.GetPlayerTransactionsPerAgent}`;
    const url = `${this.API_URL}${endpoint}`;

    return this.http.post<
      ApiResponse<{ data: ProfitPerPlayer[]; totalCount: number }>
    >(url, {
      ...filters,
    });
  }

  getRepeatedVouchersByDateRange(
    dateRange: DateRange
  ): Observable<ApiResponse<RepeatedChargesWithdrawalsVoucherByDateRange[]>> {
    const endpoint = `${ChargesWithdrawalsRequestEnum.GetRepeatedVouchersByDateRange}`;
    const url = `${this.API_URL}${endpoint}`;

    return this.http
      .post<ApiResponse<RepeatedChargesWithdrawalsVoucherByDateRange[]>>(url, {
        ...dateRange,
      })
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((item) => ({
            ...item,
            monto:
              typeof item.monto === 'string'
                ? parseFloat(item.monto)
                : item.monto,
            total:
              typeof item.total === 'string'
                ? parseInt(item.total, 10)
                : item.total,
            has_financial: Number(item.has_financial ?? 0),
          })),
        }))
      );
  }

  createReverse(
    data: ReverseChargesWithdrawalsRequestPayload
  ): Observable<MutationResponse> {
    const updatedData = {
      ...data,
    };

    return this.http.patch<MutationResponse>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.CreateReverse}`,
      updatedData
    );
  }

  createDivision(
    data: DivideChargesWithdrawalsRequestPayload
  ): Observable<MutationResponse> {
    const updatedData = {
      ...data,
    };
    return this.http.patch<MutationResponse>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.CreateDivision}`,
      updatedData
    );
  }

  getUserCreatedRegister(): Observable<UserCreatedRegisterResponse> {
    return this.http.get<UserCreatedRegisterResponse>(
      `${this.API_URL}${ChargesWithdrawalsRequestEnum.getUserCreatedRequest}`
    );
  }
}
